import { ButtonHTMLAttributes, forwardRef, useId } from "react"
import { makeStyles } from "@material-ui/core"
import clsx from "clsx"

import { colors } from "../../utils/theme"

export type CompoundResponseValue = 0 | 1 | 2 | 3 | 4 | 5

type CompoundResponseVariant = "yes" | "no"

export type CompoundResponseProps = {
  value: CompoundResponseValue
  variant?: CompoundResponseVariant
  label?: string
  className?: string
} & Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "value" | "children" | "className"
>

type PaletteStop = {
  numberBg: string
  numberText: string
  connectorHue: string
  accentStart: string
  accentEnd: string
}

// Approximates the gradient ramp that exists in the provided Figma reference.
const GRADIENT_SCALE: Record<CompoundResponseValue, PaletteStop> = {
  0: {
    numberBg: "#F5F5F5",
    numberText: "#4A4A4A",
    connectorHue: "#F2B2E3",
    accentStart: "#E85FBC",
    accentEnd: "#B40C73",
  },
  1: {
    numberBg: "#DFD0FA",
    numberText: "#3E0F90",
    connectorHue: "#E0C6FF",
    accentStart: "#CE8CFF",
    accentEnd: "#A045E2",
  },
  2: {
    numberBg: "#9676CE",
    numberText: "#FFFFFF",
    connectorHue: "#D1ACFF",
    accentStart: "#BF73FF",
    accentEnd: "#8F30E1",
  },
  3: {
    numberBg: "#6438B1",
    numberText: "#FFFFFF",
    connectorHue: "#C18BFF",
    accentStart: "#B25CF0",
    accentEnd: "#8528D4",
  },
  4: {
    numberBg: "#7930A2",
    numberText: "#FFFFFF",
    connectorHue: "#B268FF",
    accentStart: "#A343EB",
    accentEnd: "#761ACC",
  },
  5: {
    numberBg: "#AA2886",
    numberText: "#FFFFFF",
    connectorHue: "#A85AF2",
    accentStart: "#972EDC",
    accentEnd: "#6B13C1",
  },
}

const NO_LABEL_TEXT_COLOR = "#000000"

const CompoundResponse = forwardRef<HTMLButtonElement, CompoundResponseProps>(
  function CompoundResponseInner(
    { value, variant = "yes", label, disabled, className, type, ...rest },
    ref
  ) {
    const classes = useStyles()
    const gradientId = useId()
    const palette = GRADIENT_SCALE[value] ?? GRADIENT_SCALE[5]
    const resolvedLabel = label ?? (variant === "yes" ? "Yes" : "No")
    const buttonType = type ?? "button"

    const labelBackground = variant === "yes" ? "#AA2886" : "#F5F5F5"

    const gradientEndColor = variant === "yes" ? "#AA2886" : "#F5F5F5"

    return (
      <button
        {...rest}
        ref={ref}
        type={buttonType}
        className={clsx(classes.root, disabled && classes.disabled, className)}
        disabled={disabled}
      >
        <span className={classes.content}>
          <span
            className={classes.number}
            style={{
              backgroundColor: palette.numberBg,
              color: palette.numberText,
            }}
          >
            {value}
          </span>
          <svg
            aria-hidden="true"
            className={classes.connector}
            viewBox="0 0 28 23"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id={gradientId} x1="0%" x2="100%" y1="0%" y2="0%">
                <stop offset="0%" stopColor={palette.numberBg} />
                <stop offset="100%" stopColor={gradientEndColor} />
              </linearGradient>
            </defs>
            <path
              d="M0 0C9 6 19 6 28 0L28 23C19 17 9 17 0 23Z"
              fill={`url(#${gradientId})`}
            />
          </svg>
          <span
            className={clsx(classes.label, variant === "no" && classes.labelNo)}
            style={{
              //   backgroundImage: variant === "yes" ? labelBackground : undefined,
              backgroundColor: labelBackground,
            }}
          >
            <span
              className={classes.labelText}
              style={{
                color:
                  variant === "yes"
                    ? colors.neutral.white
                    : NO_LABEL_TEXT_COLOR,
              }}
            >
              {resolvedLabel}
            </span>
          </span>
        </span>
      </button>
    )
  }
)

const useStyles = makeStyles({
  root: {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    font: "inherit",
    display: "inline-flex",
    borderRadius: 999,
    "&:focus-visible $content": {
      boxShadow: "0 0 0 2px rgba(164, 81, 220, 0.4)",
    },
  },
  disabled: {
    cursor: "not-allowed",
    opacity: 0.6,
  },
  content: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    padding: "0 4px",
    height: 37,
    borderRadius: 24,
    transition: "box-shadow 0.2s ease",
  },
  number: {
    width: 34,
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    fontFamily: "Lato, sans-serif",
    fontSize: 16,
    fontWeight: 700,
    lineHeight: "20px",
    zIndex: 1,
    marginRight: 12,
    borderWidth: 2,
  },
  connector: {
    position: "absolute",
    left: 37,
    top: 7,
    width: 18,
    height: 24,
    zIndex: 0,
    display: "block",
    pointerEvents: "none",
  },
  label: {
    width: 37,
    height: 37,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 20,
    zIndex: 1,
    backgroundColor: colors.neutral.white,
  },
  labelNo: {},
  labelText: {
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    fontSize: 14,
    lineHeight: "16px",
  },
})

CompoundResponse.displayName = "CompoundResponse"

export default CompoundResponse
